import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AssetUploadField from "./AssetUploadField";
import { BRAND, emptyBrand, emptyFont, emptySlide } from "./config";
import { getValueAtPath, setValueAtPath } from "./editorUtils";
import { isManagedLandingAsset } from "../../../utils/landingAssets";

const tabs = ["Colores y estilo", "Recursos visuales", "Secciones visibles", "Datos y contacto", "Publicar"];

function ColorField({ label, value, onChange, helperText = "" }) {
  return (
    <Stack spacing={0.7}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            flex: "0 0 auto",
            borderRadius: 2,
            bgcolor: value,
            border: "1px solid rgba(25,36,47,.14)",
            boxShadow: `0 0 0 5px ${value}18`,
          }}
        />
        <TextField
          fullWidth
          size="small"
          label={label}
          type="color"
          value={value}
          onChange={onChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ style: { minHeight: 34, cursor: "pointer" } }}
        />
      </Stack>
      {helperText ? (
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", lineHeight: 1.3 }}>
          {helperText}
        </Typography>
      ) : null}
    </Stack>
  );
}

function SectionPaletteEditor({ title, value, fallback, onChange, onReset }) {
  const current = value || {};
  return (
    <Box sx={{ p: 1.7, border: "1px solid rgba(25,36,47,.10)", borderRadius: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 950 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">Dejá la sección heredada o personalizá solamente lo necesario.</Typography>
        </Box>
        <Button size="small" onClick={onReset} sx={{ textTransform: "none", fontWeight: 900 }}>
          Usar paleta general
        </Button>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <ColorField label="Fondo de sección" helperText="Color detrás de todo el bloque." value={current.backgroundColor || fallback.backgroundColor} onChange={(event) => onChange("backgroundColor", event.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ColorField label="Tarjetas o superficie" helperText="Fondo de tarjetas internas y paneles." value={current.surfaceColor || fallback.surfaceColor} onChange={(event) => onChange("surfaceColor", event.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <ColorField label="Botones y detalles" helperText="Íconos, enlaces y elementos destacados." value={current.accentColor || fallback.accentColor} onChange={(event) => onChange("accentColor", event.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ColorField label="Color de títulos" helperText="Cambia los encabezados de esta sección." value={current.titleColor || fallback.titleColor} onChange={(event) => onChange("titleColor", event.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ColorField label="Color de párrafos" helperText="Cambia descripciones y textos de lectura." value={current.textColor || fallback.textColor} onChange={(event) => onChange("textColor", event.target.value)} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default function LandingSettingsDialog({
  open,
  form,
  productsEnabled,
  saving,
  initialTab = 0,
  showTrialGuide = false,
  onClose,
  onSave,
  onUploadAsset,
  onDeleteAsset,
}) {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [tab, setTab] = React.useState(0);
  const [draft, setDraft] = React.useState(form);
  const [assetBusy, setAssetBusy] = React.useState(false);
  const [transactionError, setTransactionError] = React.useState("");
  const wasOpenRef = React.useRef(false);
  const temporaryUploadsRef = React.useRef(new Set());
  const pendingDeletesRef = React.useRef(new Set());

  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(form);
      const requestedTab = Number(initialTab);
      setTab(Number.isFinite(requestedTab) ? Math.max(0, Math.min(tabs.length - 1, requestedTab)) : 0);
      setTransactionError("");
      temporaryUploadsRef.current = new Set();
      pendingDeletesRef.current = new Set();
    }
    wasOpenRef.current = open;
  }, [open, form, initialTab]);

  const setPath = React.useCallback((path, value) => {
    setDraft((previous) => setValueAtPath(previous, path, value));
  }, []);

  const setGroup = (group, field, value) => setPath(`${group}.${field}`, value);
  const setNested = (group, nested, field, value) => setPath(`${group}.${nested}.${field}`, value);

  const deleteQuietly = React.useCallback(
    async (value) => {
      if (!value || !isManagedLandingAsset(value)) return;
      await onDeleteAsset(value).catch(() => {});
    },
    [onDeleteAsset]
  );

  const registerReplacement = React.useCallback(
    async (previousValue) => {
      if (!previousValue || !isManagedLandingAsset(previousValue)) return;

      if (temporaryUploadsRef.current.has(previousValue)) {
        temporaryUploadsRef.current.delete(previousValue);
        await deleteQuietly(previousValue);
        return;
      }

      pendingDeletesRef.current.add(previousValue);
    },
    [deleteQuietly]
  );

  const uploadToDraft = React.useCallback(
    async (path, file, metadata) => {
      setAssetBusy(true);
      setTransactionError("");
      try {
        const previousValue = getValueAtPath(draft, path, "");
        const uploaded = await onUploadAsset(file, metadata);
        await registerReplacement(previousValue);
        temporaryUploadsRef.current.add(uploaded.value);
        setPath(path, uploaded.value);
        return uploaded.value;
      } catch (error) {
        setTransactionError(error?.message || "No se pudo subir el archivo");
        throw error;
      } finally {
        setAssetBusy(false);
      }
    },
    [draft, onUploadAsset, registerReplacement, setPath]
  );

  const removeFromDraft = React.useCallback(
    async (path) => {
      setAssetBusy(true);
      setTransactionError("");
      try {
        const currentValue = getValueAtPath(draft, path, "");
        await registerReplacement(currentValue);
        setPath(path, "");
      } finally {
        setAssetBusy(false);
      }
    },
    [draft, registerReplacement, setPath]
  );

  const markItemAssetsForRemoval = React.useCallback(
    async (values) => {
      setAssetBusy(true);
      try {
        for (const value of values) await registerReplacement(value);
      } finally {
        setAssetBusy(false);
      }
    },
    [registerReplacement]
  );

  const removeArrayItem = async (path, index, assetFields = []) => {
    const items = getValueAtPath(draft, path, []);
    const item = items[index] || {};
    await markItemAssetsForRemoval(assetFields.map((field) => item[field]).filter(Boolean));
    setPath(path, items.filter((_, itemIndex) => itemIndex !== index));
  };

  const addArrayItem = (path, item, max) => {
    const items = getValueAtPath(draft, path, []);
    if (items.length >= max) return;
    setPath(path, [...items, item]);
  };

  const handleCancel = async () => {
    if (saving || assetBusy) return;
    setAssetBusy(true);
    try {
      await Promise.all([...temporaryUploadsRef.current].map(deleteQuietly));
      temporaryUploadsRef.current.clear();
      pendingDeletesRef.current.clear();
      onClose();
    } finally {
      setAssetBusy(false);
    }
  };

  const handleSave = async () => {
    setTransactionError("");
    try {
      await onSave(draft, {
        editorKey: `settings:${tab}`,
        editorType: "settings",
        tabIndex: tab,
        sectionLabel: tabs[tab] || "Ajustes globales",
      });
      await Promise.all([...pendingDeletesRef.current].map(deleteQuietly));
      temporaryUploadsRef.current.clear();
      pendingDeletesRef.current.clear();
      onClose();
    } catch (error) {
      setTransactionError(error?.message || "No se pudieron guardar los ajustes");
    }
  };

  const blocked = saving || assetBusy;
  const fonts = Array.isArray(draft.theme?.fonts) ? draft.theme.fonts : [];
  const slides = Array.isArray(draft.hero?.slides) ? draft.hero.slides : [];
  const brands = Array.isArray(draft.catalog?.brands?.items) ? draft.catalog.brands.items : [];
  const businessNeedsAttention = !String(draft.business?.name || "").trim() ||
    ![draft.business?.whatsapp, draft.business?.phone, draft.business?.email, draft.business?.address]
      .some((value) => String(value || "").trim());
  const heroSlides = Array.isArray(draft.hero?.slides) ? draft.hero.slides : [];
  const heroNeedsAttention = !String(draft.hero?.title || "").trim() ||
    ![
      draft.hero?.imageUrl,
      draft.theme?.logoUrl,
      draft.theme?.compactLogoUrl,
      ...heroSlides.map((slide) => slide?.url),
    ].some((value) => String(value || "").trim());
  const publishNeedsAttention = String(draft.publicStatus || "").toLowerCase() !== "published";
  const guidedTabIndex = showTrialGuide
    ? businessNeedsAttention
      ? 3
      : heroNeedsAttention
        ? -1
        : publishNeedsAttention
          ? 4
          : -1
    : -1;
  const guidedPanelSx = (active) => active
    ? {
        borderColor: "rgba(243,90,58,.72)",
        boxShadow: "0 0 0 0 rgba(243,90,58,.28)",
        "@keyframes guidedSettingsPanel": {
          "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.18)" },
          "50%": { boxShadow: "0 0 0 9px rgba(243,90,58,0)" },
        },
        animation: "guidedSettingsPanel 1.8s ease-in-out infinite",
      }
    : {};

  return (
    <Dialog
      open={open}
      onClose={blocked ? undefined : handleCancel}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          maxHeight: fullScreen ? "100%" : "92vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5, borderBottom: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: "-.025em" }}>
          Ajustes generales
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.60)" }}>
          Todo lo global de la web, explicado por uso: colores, imágenes, visibilidad, datos públicos y publicación.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, bgcolor: "rgba(238,240,245,.45)" }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, mx: { xs: -2, sm: -3 }, px: { xs: 1, sm: 2 }, bgcolor: "#fff", borderBottom: "1px solid rgba(25,36,47,.10)", position: "sticky", top: 0, zIndex: 2 }}
        >
          {tabs.map((label, index) => (
            <Tab
              key={label}
              label={label}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                ...(index === guidedTabIndex
                  ? {
                      color: `${BRAND.orange} !important`,
                      "@keyframes guidedSettingsTab": {
                        "0%, 100%": { bgcolor: "transparent" },
                        "50%": { bgcolor: "rgba(243,90,58,.10)" },
                      },
                      animation: "guidedSettingsTab 1.7s ease-in-out infinite",
                    }
                  : {}),
              }}
            />
          ))}
        </Tabs>

        {transactionError ? <Alert severity="error" sx={{ mb: 2 }}>{transactionError}</Alert> : null}

        {tab === 0 ? (
          <Stack spacing={2.5}>
            <Box sx={{ p: 2, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.10)", borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 950, mb: 0.5 }}>Estilo principal</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Elegí los colores viendo para qué sirve cada uno. La muestra de abajo cambia con la paleta actual.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><ColorField label="Color principal" helperText="Se usa en botones, enlaces, íconos y detalles que llaman la atención." value={draft.theme.primaryColor} onChange={(event) => setGroup("theme", "primaryColor", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><ColorField label="Color secundario" helperText="Se usa en navbar, footer, títulos fuertes y fondos de contraste." value={draft.theme.secondaryColor} onChange={(event) => setGroup("theme", "secondaryColor", event.target.value)} /></Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" select fullWidth label="Diseño de navbar" value={draft.navigation.style || "style-1"} onChange={(event) => setGroup("navigation", "style", event.target.value)}>
                    <MenuItem value="style-1">Clásica translúcida</MenuItem>
                    <MenuItem value="style-2">Comercial con barra superior</MenuItem>
                    <MenuItem value="style-3">Oscura con logo centrado</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" select fullWidth label="Diseño de footer" value={draft.footer.style || "style-1"} onChange={(event) => setGroup("footer", "style", event.target.value)}>
                    <MenuItem value="style-1">Completo oscuro</MenuItem>
                    <MenuItem value="style-2">Comercial claro</MenuItem>
                    <MenuItem value="style-3">Minimalista centrado</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: draft.theme.backgroundColor || "#f5f5f5",
                  border: `1px solid ${draft.theme.dividerColor || "rgba(25,36,47,.12)"}`,
                }}
              >
                <Typography variant="overline" sx={{ color: draft.theme.primaryColor, fontWeight: 950, letterSpacing: 1 }}>
                  MUESTRA EN VIVO
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 0.2,
                    color: draft.theme.textColor,
                    fontFamily: draft.theme.headingFontFamily || draft.theme.fontFamily,
                    fontWeight: 950,
                  }}
                >
                  Así se verá un título importante
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: draft.theme.mutedTextColor,
                    fontFamily: draft.theme.bodyFontFamily || draft.theme.fontFamily,
                  }}
                >
                  El texto secundario usa un tono más suave para mantener una lectura clara.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: draft.theme.primaryColor,
                      color: "#fff",
                      borderRadius: `${draft.theme.borderRadius || 12}px`,
                      textTransform: "none",
                      fontWeight: 950,
                      boxShadow: "none",
                      "&:hover": { bgcolor: draft.theme.primaryColor, boxShadow: "none" },
                    }}
                  >
                    Botón principal
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      color: draft.theme.secondaryColor,
                      borderColor: draft.theme.secondaryColor,
                      borderRadius: `${draft.theme.borderRadius || 12}px`,
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    Secundario
                  </Button>
                </Stack>
              </Box>

              <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={draft.footer.showLogo !== false} onChange={(event) => setGroup("footer", "showLogo", event.target.checked)} />} label="Mostrar logo en el footer" />
            </Box>

            <Accordion disableGutters elevation={0} sx={{ bgcolor: "#fff", border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Colores y forma avanzados</Typography>
                  <Typography variant="caption" color="text.secondary">Superficies, textos, divisores y radio de bordes.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}><ColorField label="Superficie" helperText="Tarjetas y bloques elevados." value={draft.theme.surfaceColor} onChange={(event) => setGroup("theme", "surfaceColor", event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={4}><ColorField label="Fondo general" helperText="Fondo detrás de todas las secciones." value={draft.theme.backgroundColor} onChange={(event) => setGroup("theme", "backgroundColor", event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={4}><ColorField label="Texto principal" helperText="Títulos y párrafos importantes." value={draft.theme.textColor} onChange={(event) => setGroup("theme", "textColor", event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={4}><ColorField label="Texto secundario" helperText="Ayudas, descripciones y datos menos importantes." value={draft.theme.mutedTextColor} onChange={(event) => setGroup("theme", "mutedTextColor", event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField size="small" select fullWidth label="Modo" value={draft.theme.mode} onChange={(event) => setGroup("theme", "mode", event.target.value)}>
                      <MenuItem value="light">Claro</MenuItem>
                      <MenuItem value="dark">Oscuro</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ px: 0.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>Redondez de botones y tarjetas</Typography>
                        <Chip size="small" label={`${draft.theme.borderRadius || 0}px`} sx={{ fontWeight: 900 }} />
                      </Stack>
                      <Slider
                        value={Number(draft.theme.borderRadius || 0)}
                        min={0}
                        max={40}
                        step={1}
                        onChange={(_, value) => setGroup("theme", "borderRadius", Number(value || 0))}
                        sx={{ color: draft.theme.primaryColor }}
                      />
                      <Typography variant="caption" color="text.secondary">0 es recto; 40 es muy redondeado.</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Color/divisor CSS" value={draft.theme.dividerColor} onChange={(event) => setGroup("theme", "dividerColor", event.target.value)} /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ bgcolor: "#fff", border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Colores por sección</Typography>
                  <Typography variant="caption" color="text.secondary">Opcional. Indicá exactamente qué fondo, título, párrafo o botón querés cambiar.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  {[
                    ["about", "Sobre nosotros"],
                    ["highlights", "Por qué elegirnos"],
                    ["contact", "Contacto"],
                  ].map(([sectionKey, sectionLabel]) => (
                    <SectionPaletteEditor
                      key={sectionKey}
                      title={sectionLabel}
                      value={draft.appearance?.[sectionKey]}
                      fallback={{
                        backgroundColor: draft.theme.backgroundColor || "#EEF0F5",
                        surfaceColor: draft.theme.surfaceColor || "#FFFFFF",
                        accentColor: draft.theme.primaryColor || BRAND.orange,
                        titleColor: draft.theme.textColor || BRAND.dark,
                        textColor: draft.theme.mutedTextColor || "#5B5D73",
                      }}
                      onChange={(field, value) => setPath(`appearance.${sectionKey}.${field}`, value)}
                      onReset={() => setPath(`appearance.${sectionKey}`, {
                        backgroundColor: "",
                        surfaceColor: "",
                        accentColor: "",
                        titleColor: "",
                        textColor: "",
                      })}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ bgcolor: "#fff", border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Tipografías</Typography>
                  <Typography variant="caption" color="text.secondary">Usá las familias del tema o cargá archivos propios solo cuando sea necesario.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><TextField size="small" fullWidth label="Familia general" value={draft.theme.fontFamily} onChange={(event) => setGroup("theme", "fontFamily", event.target.value)} /></Grid>
                  <Grid item xs={12} md={4}><TextField size="small" fullWidth label="Familia de títulos" value={draft.theme.headingFontFamily} onChange={(event) => setGroup("theme", "headingFontFamily", event.target.value)} /></Grid>
                  <Grid item xs={12} md={4}><TextField size="small" fullWidth label="Familia de textos" value={draft.theme.bodyFontFamily} onChange={(event) => setGroup("theme", "bodyFontFamily", event.target.value)} /></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="URL de Google Fonts" value={draft.theme.googleFontsUrl} onChange={(event) => setGroup("theme", "googleFontsUrl", event.target.value)} helperText="Opcional. URL pública del stylesheet de Google Fonts." /></Grid>
                </Grid>

                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {fonts.map((font, index) => (
                    <Accordion key={`font-${index}`} disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                        <Typography sx={{ fontWeight: 900 }}>{font.family || `Tipografía ${index + 1}`}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={5}><TextField size="small" fullWidth label="Nombre de familia" value={font.family} onChange={(event) => setPath(`theme.fonts.${index}.family`, event.target.value)} /></Grid>
                          <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Peso" value={font.weight} onChange={(event) => setPath(`theme.fonts.${index}.weight`, event.target.value)} /></Grid>
                          <Grid item xs={6} md={3}>
                            <TextField size="small" select fullWidth label="Estilo" value={font.style} onChange={(event) => setPath(`theme.fonts.${index}.style`, event.target.value)}>
                              <MenuItem value="normal">Normal</MenuItem>
                              <MenuItem value="italic">Italic</MenuItem>
                              <MenuItem value="oblique">Oblique</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={1} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                            <IconButton disabled={blocked} onClick={() => removeArrayItem("theme.fonts", index, ["url"])} aria-label="Eliminar tipografía"><DeleteOutlineRoundedIcon /></IconButton>
                          </Grid>
                          <Grid item xs={12}>
                            <AssetUploadField
                              label="Archivo WOFF/WOFF2"
                              kind="file"
                              compact
                              value={font.url}
                              disabled={blocked}
                              accept=".woff,.woff2,font/woff,font/woff2,application/font-woff,application/octet-stream"
                              helperText="El backend valida la firma real del archivo antes de guardarlo."
                              onUpload={async (file) => {
                                const value = await uploadToDraft(`theme.fonts.${index}.url`, file, { sectionKey: "fonts", assetKey: `font-${index + 1}` });
                                setPath(`theme.fonts.${index}.format`, file.name.toLowerCase().endsWith(".woff") ? "woff" : "woff2");
                                return value;
                              }}
                              onRemove={() => removeFromDraft(`theme.fonts.${index}.url`)}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  <Button type="button" startIcon={<AddRoundedIcon />} disabled={blocked || fonts.length >= 12} onClick={() => addArrayItem("theme.fonts", { ...emptyFont }, 12)} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
                    Agregar tipografía propia
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        ) : null}

        {tab === 1 ? (
          <Stack spacing={2.5}>
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography sx={{ fontWeight: 950 }}>Marca principal</Typography></AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AssetUploadField label="Logo principal" value={draft.theme.logoUrl} disabled={blocked} helperText="SVG, PNG, WebP o JPG. Logo completo para navbar y footer." onUpload={(file) => uploadToDraft("theme.logoUrl", file, { sectionKey: "branding", assetKey: "logo" })} onRemove={() => removeFromDraft("theme.logoUrl")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AssetUploadField label="Logo compacto" value={draft.theme.compactLogoUrl} disabled={blocked} helperText="SVG, PNG o WebP. Ícono o versión reducida para pantallas pequeñas." onUpload={(file) => uploadToDraft("theme.compactLogoUrl", file, { sectionKey: "branding", assetKey: "compact-logo" })} onRemove={() => removeFromDraft("theme.compactLogoUrl")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AssetUploadField label="Favicon" value={draft.theme.faviconUrl} disabled={blocked} accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico" helperText="SVG, PNG, WebP o ICO. Preferentemente cuadrado." onUpload={(file) => uploadToDraft("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" })} onRemove={() => removeFromDraft("theme.faviconUrl")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AssetUploadField label="Imagen SEO" value={draft.seo.imageUrl} disabled={blocked} helperText="Imagen que se usa al compartir la web." onUpload={(file) => uploadToDraft("seo.imageUrl", file, { sectionKey: "seo", assetKey: "share-image" })} onRemove={() => removeFromDraft("seo.imageUrl")} />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography sx={{ fontWeight: 950 }}>Hero y carrusel</Typography></AccordionSummary>
              <AccordionDetails>
                <AssetUploadField label="Imagen principal de respaldo" value={draft.hero.imageUrl} disabled={blocked} helperText="Se muestra cuando no hay slides activas." onUpload={(file) => uploadToDraft("hero.imageUrl", file, { sectionKey: "hero", assetKey: "background" })} onRemove={() => removeFromDraft("hero.imageUrl")} />
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Posición de la imagen" value={draft.hero.imagePosition} onChange={(event) => setPath("hero.imagePosition", event.target.value)}>
                      <MenuItem value="center">Centro</MenuItem>
                      <MenuItem value="top">Arriba</MenuItem>
                      <MenuItem value="bottom">Abajo</MenuItem>
                      <MenuItem value="left">Izquierda</MenuItem>
                      <MenuItem value="right">Derecha</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={8}><TextField fullWidth label="Overlay CSS" value={draft.hero.overlay} onChange={(event) => setPath("hero.overlay", event.target.value)} helperText="Ejemplo: linear-gradient(to bottom, rgba(0,0,0,.1), rgba(0,0,0,.7))" /></Grid>
                  <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Frases rotativas" value={(draft.hero.rotatingPhrases || []).join("\n")} onChange={(event) => setPath("hero.rotatingPhrases", event.target.value.split("\n").map((item) => item.trim()).filter(Boolean))} helperText="Una frase por línea." /></Grid>
                </Grid>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {slides.map((slide, index) => (
                    <PaperSlide
                      key={`slide-${index}`}
                      index={index}
                      slide={slide}
                      blocked={blocked}
                      setPath={setPath}
                      uploadToDraft={uploadToDraft}
                      removeFromDraft={removeFromDraft}
                      onRemove={() => removeArrayItem("hero.slides", index, ["url"])}
                    />
                  ))}
                  <Button type="button" startIcon={<AddRoundedIcon />} disabled={blocked || slides.length >= 6} onClick={() => addArrayItem("hero.slides", { ...emptySlide }, 6)} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
                    Agregar slide
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography sx={{ fontWeight: 950 }}>Spotlight de marca</Typography></AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  El texto, los botones, la activación y los colores se editan desde la sección Campaña o bloque especial.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AssetUploadField label="Logo del spotlight" value={draft.catalog.spotlight.logoUrl} disabled={blocked} onUpload={(file) => uploadToDraft("catalog.spotlight.logoUrl", file, { sectionKey: "spotlight", assetKey: "logo" })} onRemove={() => removeFromDraft("catalog.spotlight.logoUrl")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AssetUploadField label="Fondo del spotlight" value={draft.catalog.spotlight.backgroundImageUrl} disabled={blocked} onUpload={(file) => uploadToDraft("catalog.spotlight.backgroundImageUrl", file, { sectionKey: "spotlight", assetKey: "background" })} onRemove={() => removeFromDraft("catalog.spotlight.backgroundImageUrl")} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Texto alternativo del logo" value={draft.catalog.spotlight.logoAlt || ""} onChange={(event) => setPath("catalog.spotlight.logoAlt", event.target.value)} helperText="Describí el logo para accesibilidad." />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Sobre nosotros</Typography>
                  <Typography variant="caption" color="text.secondary">Foto institucional o imagen del negocio.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <AssetUploadField
                      label="Imagen institucional"
                      value={draft.about.imageUrl}
                      disabled={blocked}
                      helperText="Usá una foto real del negocio, equipo, local o trabajo realizado."
                      onUpload={(file) => uploadToDraft("about.imageUrl", file, { sectionKey: "about", assetKey: "main-image" })}
                      onRemove={() => removeFromDraft("about.imageUrl")}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth label="Texto alternativo" value={draft.about.imageAlt || ""} onChange={(event) => setPath("about.imageAlt", event.target.value)} helperText="Describí brevemente qué muestra la foto." />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Fondo de contacto</Typography>
                  <Typography variant="caption" color="text.secondary">Imagen opcional para acompañar los datos y el mapa.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <AssetUploadField
                  label="Imagen de fondo de contacto"
                  value={draft.contact.backgroundImageUrl}
                  disabled={blocked}
                  helperText="Elegí una imagen con poco ruido visual para no dificultar la lectura."
                  onUpload={(file) => uploadToDraft("contact.backgroundImageUrl", file, { sectionKey: "contact", assetKey: "background" })}
                  onRemove={() => removeFromDraft("contact.backgroundImageUrl")}
                />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Imágenes de diferenciales ({(draft.highlights || []).length})</Typography>
                  <Typography variant="caption" color="text.secondary">Opcionales. Primero cargá los diferenciales desde su sección.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  {(draft.highlights || []).map((item, index) => (
                    <Box key={item.id || `highlight-asset-${index}`} sx={{ p: 1.5, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5 }}>
                      <Typography sx={{ fontWeight: 900, mb: 1.2 }}>{item.title || `Diferencial ${index + 1}`}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                          <AssetUploadField
                            compact
                            label="Imagen"
                            value={item.imageUrl}
                            disabled={blocked}
                            onUpload={(file) => uploadToDraft(`highlights.${index}.imageUrl`, file, { sectionKey: "highlights", assetKey: item.id || `item-${index + 1}` })}
                            onRemove={() => removeFromDraft(`highlights.${index}.imageUrl`)}
                          />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <TextField fullWidth label="Texto alternativo" value={item.imageAlt || ""} onChange={(event) => setPath(`highlights.${index}.imageAlt`, event.target.value)} />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  {(draft.highlights || []).length === 0 ? <Alert severity="info">Todavía no hay diferenciales para asociar imágenes.</Alert> : null}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography sx={{ fontWeight: 950 }}>Logos de marcas ({brands.length})</Typography></AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  {brands.map((brand, index) => (
                    <Box key={`brand-${index}`} sx={{ p: 1.5, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontWeight: 900 }}>{brand.name || `Marca ${index + 1}`}</Typography>
                        <IconButton disabled={blocked} onClick={() => removeArrayItem("catalog.brands.items", index, ["logoUrl"])} aria-label="Eliminar marca"><DeleteOutlineRoundedIcon /></IconButton>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Nombre" value={brand.name} onChange={(event) => setPath(`catalog.brands.items.${index}.name`, event.target.value)} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Búsqueda o slug" value={brand.query} onChange={(event) => setPath(`catalog.brands.items.${index}.query`, event.target.value)} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Texto alternativo" value={brand.alt} onChange={(event) => setPath(`catalog.brands.items.${index}.alt`, event.target.value)} /></Grid>
                        <Grid item xs={12} md={6}><AssetUploadField compact label="Logo" value={brand.logoUrl} disabled={blocked} onUpload={(file) => uploadToDraft(`catalog.brands.items.${index}.logoUrl`, file, { sectionKey: "brands", assetKey: `brand-${index + 1}` })} onRemove={() => removeFromDraft(`catalog.brands.items.${index}.logoUrl`)} /></Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button type="button" startIcon={<AddRoundedIcon />} disabled={blocked || brands.length >= 40} onClick={() => addArrayItem("catalog.brands.items", { ...emptyBrand }, 40)} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
                    Agregar marca
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        ) : null}

        {tab === 2 ? (
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)", mb: 1.5 }}>
              Activá únicamente las secciones que aportan valor. Lo que apagues no se mostrará en la web pública.
            </Typography>
            <Grid container spacing={1}>
              {[
                ["showNavbar", "Navbar"],
                ["showHero", "Hero"],
                ["showProducts", "Bloques del catálogo"],
                ["showSpotlight", "Spotlight"],
                ["showFeatured", "Productos destacados"],
                ["showBrands", "Marcas"],
                ["showCategories", "Categorías"],
                ["showAbout", "Nosotros"],
                ["showHighlights", "Diferenciales"],
                ["showContact", "Contacto"],
                ["showFooter", "Footer"],
                ["showFloatingWhatsapp", "WhatsApp flotante"],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={6} key={field}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(draft.sections[field])}
                        disabled={!productsEnabled && ["showSpotlight", "showBrands", "showProducts", "showFeatured", "showCategories"].includes(field)}
                        onChange={(event) => setGroup("sections", field, event.target.checked)}
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={<Switch checked={draft.catalog.spotlight.enabled === true} disabled={!productsEnabled} onChange={(event) => setPath("catalog.spotlight.enabled", event.target.checked)} />}
              label="Spotlight configurado y habilitado"
            />
          </Box>
        ) : null}

        {tab === 3 ? (
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2,
                bgcolor: "#fff",
                border: "1px solid rgba(25,36,47,.10)",
                borderRadius: 3,
                ...guidedPanelSx(showTrialGuide && businessNeedsAttention),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: .5 }}>
                <Typography variant="h6" sx={{ fontWeight: 950 }}>Datos públicos</Typography>
                {showTrialGuide && businessNeedsAttention ? (
                  <Chip size="small" label="COMPLETÁ ESTO AHORA" sx={{ bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, fontWeight: 950, fontSize: 10 }} />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se reutilizan automáticamente en navbar, contacto, footer y WhatsApp.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField size="small" fullWidth label="Nombre del negocio" value={draft.business.name || ""} onChange={(event) => setGroup("business", "name", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="WhatsApp" value={draft.business.whatsapp} onChange={(event) => setGroup("business", "whatsapp", event.target.value)} helperText="Incluí el código de país" /></Grid>
                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Teléfono" value={draft.business.phone} onChange={(event) => setGroup("business", "phone", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Email" value={draft.business.email} onChange={(event) => setGroup("business", "email", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Horarios" value={draft.business.schedule} onChange={(event) => setGroup("business", "schedule", event.target.value)} /></Grid>
                <Grid item xs={12}><TextField size="small" fullWidth label="Dirección" value={draft.business.address} onChange={(event) => setGroup("business", "address", event.target.value)} /></Grid>
                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Ciudad" value={draft.business.city} onChange={(event) => setGroup("business", "city", event.target.value)} /></Grid>
                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Provincia" value={draft.business.province} onChange={(event) => setGroup("business", "province", event.target.value)} /></Grid>
                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="País" value={draft.business.country} onChange={(event) => setGroup("business", "country", event.target.value)} /></Grid>
                <Grid item xs={12}><TextField size="small" fullWidth label="Google Maps Embed URL" value={draft.business.mapEmbedUrl} onChange={(event) => setGroup("business", "mapEmbedUrl", event.target.value)} /></Grid>
              </Grid>
            </Box>

            <Accordion disableGutters elevation={0} sx={{ bgcolor: "#fff", border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>Redes sociales</Typography>
                  <Typography variant="caption" color="text.secondary">Completá únicamente las que realmente utiliza el negocio.</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {[
                    ["instagram", "Instagram"],
                    ["facebook", "Facebook"],
                    ["tiktok", "TikTok"],
                    ["linkedin", "LinkedIn"],
                    ["youtube", "YouTube"],
                    ["website", "Sitio externo"],
                  ].map(([key, label]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField size="small" fullWidth label={label} value={draft.business.socials[key]} onChange={(event) => setNested("business", "socials", key, event.target.value)} />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        ) : null}

        {tab === 4 ? (
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2,
                bgcolor: "#fff",
                border: "1px solid rgba(25,36,47,.10)",
                borderRadius: 3,
                ...guidedPanelSx(showTrialGuide && !businessNeedsAttention && !heroNeedsAttention && publishNeedsAttention),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 950 }}>Publicación</Typography>
                {showTrialGuide && !businessNeedsAttention && !heroNeedsAttention && publishNeedsAttention ? (
                  <Chip size="small" label="ÚLTIMO PASO" sx={{ bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, fontWeight: 950, fontSize: 10 }} />
                ) : null}
              </Stack>
              <TextField size="small" select fullWidth label="Estado público" value={draft.publicStatus} onChange={(event) => setDraft((previous) => ({ ...previous, publicStatus: event.target.value }))}>
                <MenuItem value="draft">Borrador · solo visible en el panel</MenuItem>
                <MenuItem value="published">Publicada · disponible para clientes</MenuItem>
                <MenuItem value="maintenance">Mantenimiento · muestra un aviso temporal</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ p: 2, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.10)", borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 950, mb: 0.5 }}>SEO básico</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                La imagen para compartir se administra una sola vez desde la pestaña Archivos.
              </Typography>
              <Stack spacing={2}>
                <TextField size="small" fullWidth label="Título SEO" value={draft.seo.title} onChange={(event) => setGroup("seo", "title", event.target.value)} helperText={`${String(draft.seo.title || "").length}/60 caracteres`} />
                <TextField size="small" fullWidth multiline minRows={3} label="Descripción SEO" value={draft.seo.description} onChange={(event) => setGroup("seo", "description", event.target.value)} helperText={`${String(draft.seo.description || "").length}/160 caracteres`} />
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.75, borderTop: "1px solid rgba(25,36,47,.10)", bgcolor: "#fff" }}>
        <Button onClick={handleCancel} disabled={blocked} sx={{ textTransform: "none", fontWeight: 900 }}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={<SaveRoundedIcon />}
          disabled={blocked}
          onClick={handleSave}
          sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, borderRadius: 999, px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#dc482c", boxShadow: "none" } }}
        >
          {saving ? "Guardando..." : assetBusy ? "Procesando archivo..." : "Guardar ajustes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PaperSlide({ index, slide, blocked, setPath, uploadToDraft, removeFromDraft, onRemove }) {
  return (
    <Box sx={{ p: 1.5, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 900 }}>Slide {index + 1}</Typography>
        <IconButton disabled={blocked} onClick={onRemove} aria-label="Eliminar slide"><DeleteOutlineRoundedIcon /></IconButton>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <AssetUploadField compact label="Imagen" value={slide.url} disabled={blocked} onUpload={(file) => uploadToDraft(`hero.slides.${index}.url`, file, { sectionKey: "hero", assetKey: `slide-${index + 1}` })} onRemove={() => removeFromDraft(`hero.slides.${index}.url`)} />
        </Grid>
        <Grid item xs={12} md={7}>
          <Stack spacing={1.5}>
            <TextField fullWidth label="Título opcional" value={slide.title} onChange={(event) => setPath(`hero.slides.${index}.title`, event.target.value)} />
            <TextField fullWidth label="Subtítulo opcional" value={slide.subtitle} onChange={(event) => setPath(`hero.slides.${index}.subtitle`, event.target.value)} />
            <TextField fullWidth label="Texto alternativo" value={slide.alt} onChange={(event) => setPath(`hero.slides.${index}.alt`, event.target.value)} />
            <TextField select fullWidth label="Posición" value={slide.position} onChange={(event) => setPath(`hero.slides.${index}.position`, event.target.value)}>
              <MenuItem value="center">Centro</MenuItem>
              <MenuItem value="top">Arriba</MenuItem>
              <MenuItem value="bottom">Abajo</MenuItem>
              <MenuItem value="left">Izquierda</MenuItem>
              <MenuItem value="right">Derecha</MenuItem>
            </TextField>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
